import type {
  BuilderDocument,
  BuilderExportTarget,
  BuilderSection,
  BuilderValidationIssue,
  BuilderValidationSummary
} from '@shared/types';
import { slugifyLabel } from '@shared/utils/slug';

function createIssue(input: Omit<BuilderValidationIssue, 'id'>): BuilderValidationIssue {
  const cryptoRef = globalThis.crypto;
  const id =
    cryptoRef && typeof cryptoRef.randomUUID === 'function'
      ? cryptoRef.randomUUID()
      : Math.random().toString(36).slice(2, 10);

  return {
    id,
    ...input
  };
}

function validateLinkPair(
  issues: BuilderValidationIssue[],
  section: BuilderSection,
  basePath: string,
  label: string,
  href: string,
  target: BuilderExportTarget | 'all' = 'all'
): void {
  const hasLabel = label.trim().length > 0;
  const hasHref = href.trim().length > 0;
  if (hasLabel !== hasHref) {
    issues.push(
      createIssue({
        level: 'error',
        scope: 'section',
        code: 'link_pair_incomplete',
        fieldPath: basePath,
        message: 'CTA text and link must both be provided.',
        target,
        sectionId: section.id
      })
    );
  }
}

function validateRequiredText(
  issues: BuilderValidationIssue[],
  section: BuilderSection,
  fieldPath: string,
  value: string,
  message: string
): void {
  if (value.trim().length === 0) {
    issues.push(
      createIssue({
        level: 'error',
        scope: 'section',
        code: 'required_text',
        fieldPath,
        message,
        target: 'all',
        sectionId: section.id
      })
    );
  }
}

function validateRepeaterCount(
  issues: BuilderValidationIssue[],
  section: BuilderSection,
  fieldPath: string,
  count: number,
  message: string
): void {
  if (count === 0) {
    issues.push(
      createIssue({
        level: 'error',
        scope: 'section',
        code: 'empty_repeater',
        fieldPath,
        message,
        target: 'all',
        sectionId: section.id
      })
    );
  }
}

function validateAssetReference(
  issues: BuilderValidationIssue[],
  section: BuilderSection,
  fieldPath: string,
  assetId: string | null,
  assetIds: Set<string>
): void {
  if (assetId && !assetIds.has(assetId)) {
    issues.push(
      createIssue({
        level: 'warning',
        scope: 'section',
        code: 'missing_asset',
        fieldPath,
        message: 'The selected asset could not be found in project resources.',
        target: 'all',
        sectionId: section.id
      })
    );
  }
}

function validateSection(section: BuilderSection, assetIds: Set<string>): BuilderValidationIssue[] {
  const issues: BuilderValidationIssue[] = [];

  switch (section.type) {
    case 'hero':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'Hero heading is required.');
      validateRequiredText(issues, section, 'props.subheading', section.props.subheading, 'Hero subheading is required.');
      validateLinkPair(
        issues,
        section,
        'props.primaryCta',
        section.props.primaryCta.label,
        section.props.primaryCta.href
      );
      validateLinkPair(
        issues,
        section,
        'props.secondaryCta',
        section.props.secondaryCta.label,
        section.props.secondaryCta.href
      );
      validateAssetReference(issues, section, 'props.media.assetId', section.props.media.assetId, assetIds);
      break;
    case 'about':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'About heading is required.');
      validateRequiredText(issues, section, 'props.body', section.props.body, 'About body is required.');
      break;
    case 'services':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'Services heading is required.');
      validateRepeaterCount(issues, section, 'props.items', section.props.items.length, 'Add at least one service item.');
      section.props.items.forEach((item, index) => {
        if (item.title.trim().length === 0 || item.description.trim().length === 0) {
          issues.push(
            createIssue({
              level: 'error',
              scope: 'section',
              code: 'service_item_incomplete',
              fieldPath: `props.items.${index}`,
              message: 'Each service item needs a title and description.',
              target: 'all',
              sectionId: section.id
            })
          );
        }
        validateAssetReference(issues, section, `props.items.${index}.icon.assetId`, item.icon.assetId, assetIds);
      });
      break;
    case 'features':
    case 'steps':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'Section heading is required.');
      validateRepeaterCount(issues, section, 'props.items', section.props.items.length, 'Add at least one item.');
      break;
    case 'stats':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'Stats heading is required.');
      validateRepeaterCount(issues, section, 'props.items', section.props.items.length, 'Add at least one stat.');
      break;
    case 'testimonials':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'Testimonials heading is required.');
      validateRepeaterCount(issues, section, 'props.items', section.props.items.length, 'Add at least one testimonial.');
      break;
    case 'faq':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'FAQ heading is required.');
      validateRepeaterCount(issues, section, 'props.items', section.props.items.length, 'Add at least one FAQ item.');
      break;
    case 'cta':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'CTA heading is required.');
      validateRequiredText(issues, section, 'props.body', section.props.body, 'CTA copy is required.');
      validateLinkPair(
        issues,
        section,
        'props.primaryCta',
        section.props.primaryCta.label,
        section.props.primaryCta.href
      );
      validateLinkPair(
        issues,
        section,
        'props.secondaryCta',
        section.props.secondaryCta.label,
        section.props.secondaryCta.href
      );
      break;
    case 'contact':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'Contact heading is required.');
      if (section.props.email.trim().length === 0 && section.props.phone.trim().length === 0) {
        issues.push(
          createIssue({
            level: 'warning',
            scope: 'section',
            code: 'contact_missing_channels',
            fieldPath: 'props.email',
            message: 'Add at least one contact method.',
            target: 'all',
            sectionId: section.id
          })
        );
      }
      break;
    case 'gallery':
      validateRequiredText(issues, section, 'props.heading', section.props.heading, 'Gallery heading is required.');
      validateRepeaterCount(issues, section, 'props.items', section.props.items.length, 'Add at least one gallery image.');
      section.props.items.forEach((item, index) => {
        validateAssetReference(issues, section, `props.items.${index}.image.assetId`, item.image.assetId, assetIds);
      });
      break;
    case 'footer':
      validateRequiredText(issues, section, 'props.companyName', section.props.companyName, 'Footer company name is required.');
      break;
  }

  return issues;
}

export function validateBuilderDocument(
  document: BuilderDocument,
  options: {
    assetIds?: string[];
    requestedTarget?: BuilderExportTarget | null;
    now?: string;
  } = {}
): BuilderValidationSummary {
  const issues: BuilderValidationIssue[] = [];
  const assetIds = new Set(options.assetIds ?? []);
  const checkedAt = options.now ?? new Date().toISOString();

  if (document.pageMeta.title.trim().length === 0) {
    issues.push(
      createIssue({
        level: 'error',
        scope: 'page',
        code: 'page_title_required',
        fieldPath: 'pageMeta.title',
        message: 'Page title is required.',
        target: 'all',
        sectionId: null
      })
    );
  }

  if (document.pageMeta.slug.trim().length === 0) {
    issues.push(
      createIssue({
        level: 'error',
        scope: 'page',
        code: 'page_slug_required',
        fieldPath: 'pageMeta.slug',
        message: 'Page slug is required.',
        target: 'all',
        sectionId: null
      })
    );
  } else if (document.pageMeta.slug !== slugifyLabel(document.pageMeta.slug)) {
    issues.push(
      createIssue({
        level: 'warning',
        scope: 'page',
        code: 'page_slug_sanitized',
        fieldPath: 'pageMeta.slug',
        message: 'Slug should use lowercase letters, numbers, and hyphens only.',
        target: 'all',
        sectionId: null
      })
    );
  }

  if (options.requestedTarget && !document.compatibility.targets.includes(options.requestedTarget)) {
    issues.push(
      createIssue({
        level: 'error',
        scope: 'page',
        code: 'target_not_supported',
        fieldPath: 'compatibility.targets',
        message: `This page is not marked as compatible with ${options.requestedTarget}.`,
        target: options.requestedTarget,
        sectionId: null
      })
    );
  }

  if (document.sections.length === 0) {
    issues.push(
      createIssue({
        level: 'warning',
        scope: 'page',
        code: 'page_empty',
        fieldPath: 'sections',
        message: 'Add at least one section to the page.',
        target: 'all',
        sectionId: null
      })
    );
  }

  document.sections.forEach((section) => {
    issues.push(...validateSection(section, assetIds));
  });

  const sectionStates: Record<string, BuilderValidationSummary['sectionStates'][string]> = {};
  document.sections.forEach((section) => {
    sectionStates[section.id] = 'valid';
  });

  for (const issue of issues) {
    if (!issue.sectionId) {
      continue;
    }

    const currentState = sectionStates[issue.sectionId] ?? 'unknown';
    if (issue.level === 'error') {
      sectionStates[issue.sectionId] = 'error';
    } else if (currentState !== 'error') {
      sectionStates[issue.sectionId] = 'warning';
    }
  }

  const errorCount = issues.filter((issue) => issue.level === 'error').length;
  const warningCount = issues.filter((issue) => issue.level === 'warning').length;

  return {
    isValid: errorCount === 0,
    errorCount,
    warningCount,
    issues,
    sectionStates,
    checkedAt
  };
}
